import { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, BadgeCheck, Info, Leaf, ShieldQuestion, Sparkles, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";
import { COUNTRIES, getCountry } from "@/lib/savings-model";
import { calculateCarbon, type CarbonInput, type PriceRange } from "@/lib/carbon-model";

const money = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(value));

const number = (value: number, digits = 0) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: digits }).format(value);

const range = (value: PriceRange) => `${money(value.low)} - ${money(value.high)}`;

const fieldClass =
  "w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 26 }, (_, index) => CURRENT_YEAR - index);

type CarbonCalculatorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const CarbonCalculator = ({ open, onOpenChange }: CarbonCalculatorProps) => {
  const { t } = useLanguage();
  const uid = useId();

  const [unit, setUnit] = useState<"kW" | "MW">("MW");
  const [capacity, setCapacity] = useState(10);
  const [countryCode, setCountryCode] = useState("IL");
  const [region, setRegion] = useState(getCountry("IL").regions[0].name);
  const [installYear, setInstallYear] = useState(CURRENT_YEAR - 2);
  const [showOptional, setShowOptional] = useState(false);
  const [productionInput, setProductionInput] = useState("");
  const [certification, setCertification] = useState<CarbonInput["certification"]>("none");

  const result = useMemo(
    () =>
      calculateCarbon({
        countryCode,
        region,
        unit,
        capacity,
        installYear,
        annualProductionMwh: productionInput ? Number(productionInput) : undefined,
        certification,
      }),
    [countryCode, region, unit, capacity, installYear, productionInput, certification],
  );

  const country = getCountry(countryCode);

  const certificationOptions: { value: CarbonInput["certification"]; label: string }[] = [
    { value: "none", label: t("None yet") },
    { value: "irec", label: t("Already issuing renewable energy certificates") },
    { value: "carbon", label: t("Already registered under a carbon crediting standard") },
    { value: "unknown", label: t("Not sure") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto p-5 sm:p-7">
        <DialogHeader className="text-start">
          <DialogTitle className="text-2xl font-bold">
            {t("What could your portfolio's carbon and certificate value be worth?")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Answer a few quick questions for an indicative view of avoided emissions, eligibility and potential proceeds. No registration needed.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Inputs */}
          <div className="rounded-xl border border-border/60 bg-card/60 p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-country`} className="text-xs text-muted-foreground">
                  {t("Country")}
                </Label>
                <select
                  id={`${uid}-country`}
                  className={fieldClass}
                  value={countryCode}
                  onChange={(event) => {
                    setCountryCode(event.target.value);
                    setRegion(getCountry(event.target.value).regions[0].name);
                  }}
                >
                  {COUNTRIES.map((item) => (
                    <option key={item.code} value={item.code}>
                      {t(item.name)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-region`} className="text-xs text-muted-foreground">
                  {t("Region")}
                </Label>
                <select
                  id={`${uid}-region`}
                  className={fieldClass}
                  value={region}
                  onChange={(event) => setRegion(event.target.value)}
                >
                  {country.regions.map((item) => (
                    <option key={item.name} value={item.name}>
                      {t(item.name)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-capacity`} className="text-xs text-muted-foreground">
                  {`${t("Total installed solar capacity")} (${unit})`}
                </Label>
                <div className="flex gap-1.5">
                  <input
                    id={`${uid}-capacity`}
                    type="number"
                    min={0}
                    inputMode="decimal"
                    className={cn(fieldClass, "min-w-0 flex-1")}
                    value={capacity}
                    onChange={(event) => setCapacity(Math.max(0, Number(event.target.value)))}
                  />
                  <div
                    className="flex shrink-0 overflow-hidden rounded-lg border border-border"
                    role="group"
                    aria-label={t("Capacity unit")}
                  >
                    {(["kW", "MW"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setUnit(option)}
                        aria-pressed={unit === option}
                        className={cn(
                          "px-2.5 text-xs font-semibold",
                          unit === option ? "bg-primary/20 text-primary" : "text-muted-foreground",
                        )}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-year`} className="text-xs text-muted-foreground">
                  {t("Installation year")}
                </Label>
                <select
                  id={`${uid}-year`}
                  className={fieldClass}
                  value={installYear}
                  onChange={(event) => setInstallYear(Number(event.target.value))}
                >
                  {YEARS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowOptional((value) => !value)}
              className="mt-4 text-xs font-semibold text-primary hover:underline"
              aria-expanded={showOptional}
            >
              {showOptional ? t("Hide optional details") : t("Improve my estimate (optional)")}
            </button>

            {showOptional && (
              <div className="mt-4 grid gap-3 border-t border-border/60 pt-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`${uid}-production`} className="text-xs text-muted-foreground">
                    {t("Actual annual production (MWh)")}
                  </Label>
                  <input
                    id={`${uid}-production`}
                    type="number"
                    min={0}
                    inputMode="decimal"
                    className={fieldClass}
                    placeholder={number(result.baselineMwh)}
                    value={productionInput}
                    onChange={(event) => setProductionInput(event.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t("Default: modelled from your capacity and location.")}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`${uid}-certification`} className="text-xs text-muted-foreground">
                    {t("Existing environmental certification")}
                  </Label>
                  <select
                    id={`${uid}-certification`}
                    className={fieldClass}
                    value={certification}
                    onChange={(event) => setCertification(event.target.value as CarbonInput["certification"])}
                  >
                    {certificationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Avoided emissions */}
          <div className="rounded-2xl border border-primary/25 bg-gradient-to-b from-primary/10 to-transparent p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("Estimated annual avoided emissions")}
            </p>
            <p className="mt-2 flex flex-wrap items-baseline gap-x-2 text-4xl font-bold text-foreground sm:text-5xl">
              <span className="text-primary" dir="ltr">{`${number(result.avoidedTonnes)} tCO2e`}</span>
              <span className="text-sm font-medium text-muted-foreground">{t("per year")}</span>
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {`${t("Based on")} ${number(result.baselineMwh)} MWh ${t("per year")} ${t("and a grid emission factor of")} ${result.gridFactor.toFixed(
                2,
              )} kgCO2e/kWh.`}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {t(
                "Avoided emissions do not automatically become tradable carbon credits. Credits exist only once a recognised standard approves the project and a registry issues verified units.",
              )}
            </p>
          </div>

          {/* Carbon credits */}
          <div className="rounded-xl border border-border/60 bg-card/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Leaf className="h-4 w-4" style={{ color: "hsl(var(--recovery-green))" }} aria-hidden="true" />
                {t("Carbon credits")}
              </p>
              <span
                className={cn(
                  "shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
                  result.credits.eligibility === "supported"
                    ? "border-primary/40 text-primary"
                    : "border-border text-muted-foreground",
                )}
              >
                {result.credits.eligibility === "supported"
                  ? t("Preliminary indication: pathway may be available")
                  : t("Eligibility assessment required")}
              </span>
            </div>

            {result.credits.grossRevenue ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-border/60 bg-background/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Potential annual gross revenue")}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-foreground" dir="ltr">
                    {range(result.credits.grossRevenue)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/60 bg-background/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Certification and management costs")}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-foreground" dir="ltr">
                    {range(result.credits.costs as PriceRange)}
                  </p>
                </div>
                <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Estimated net proceeds")}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-primary" dir="ltr">
                    {range(result.credits.netProceeds as PriceRange)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {t(
                  "No revenue figure is shown because eligibility or verified pricing is unknown for this market and asset profile.",
                )}
              </p>
            )}

            <p className="mt-3 text-xs text-muted-foreground">{t(result.credits.reason)}</p>
          </div>

          {/* Renewable energy certificates */}
          <div className="rounded-xl border border-border/60 bg-card/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <BadgeCheck className="h-4 w-4 text-primary" aria-hidden="true" />
                <span>
                  {`${t("Renewable energy certificates")} - `}
                  <span dir="ltr" className="whitespace-nowrap">
                    {result.certificates.name}
                  </span>
                </span>
              </p>
              <span
                className={cn(
                  "shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
                  result.certificates.eligibility === "supported"
                    ? "border-primary/40 text-primary"
                    : "border-border text-muted-foreground",
                )}
              >
                {result.certificates.eligibility === "supported"
                  ? t("Preliminary indication: pathway may be available")
                  : t("Eligibility assessment required")}
              </span>
            </div>

            <p className="mt-2 text-xs text-muted-foreground">
              {`${t("Indicative volume")}: ${number(result.certificates.volume)} ${t(
                "certificates per year (1 certificate = 1 MWh)",
              )}.`}
            </p>

            {result.certificates.unitPrice ? (
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="text-foreground">
                  {`${t("Market price used")}: `}
                  <span dir="ltr" className="whitespace-nowrap">
                    {`$${result.certificates.unitPrice.low.toFixed(2)} - $${result.certificates.unitPrice.high.toFixed(
                      2,
                    )}`}
                  </span>
                  {" / MWh"}
                </span>
                {result.certificates.priceSource ? ` - ${t(result.certificates.priceSource)}.` : "."}
              </p>
            ) : null}

            {result.certificates.grossRevenue ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-border/60 bg-background/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Potential annual gross revenue")}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-foreground" dir="ltr">
                    {range(result.certificates.grossRevenue)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/60 bg-background/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Certification and management costs")}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-foreground" dir="ltr">
                    {range(result.certificates.costs as PriceRange)}
                  </p>
                </div>
                <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Estimated net proceeds")}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-primary" dir="ltr">
                    {range(result.certificates.netProceeds as PriceRange)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {t(
                  result.certificates.reason ??
                    "No revenue figure is shown because eligibility or verified pricing is unknown for this market and asset profile.",
                )}
              </p>

            )}
          </div>

          {/* Assumptions */}
          <div className="rounded-xl border border-border/60 bg-muted/10 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Info className="h-4 w-4 text-primary" aria-hidden="true" />
              {t("Assumptions behind this estimate")}
            </p>
            <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
              <li>
                {result.usedActualProduction
                  ? t("Baseline production: the annual figure you entered.")
                  : `${t("Location-specific yield used")}: ${number(result.specificYield)} kWh/kWp ${t(
                      "per year",
                    )} (${t("editable default")}).`}
              </li>
              <li>
                {t(
                  "Carbon credits and renewable energy certificates are separate products with separate eligibility rules. Selling certificates can prevent claiming credits for the same MWh.",
                )}
              </li>
              <li>
                {t(
                  "Costs cover validation, registration, annual verification, registry fees and a management and sales share of gross proceeds.",
                )}
              </li>
              <li>
                {t(
                  "Prices are indicative market ranges and change constantly. Nothing here is an offer, a valuation or a guarantee of revenue.",
                )}
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-foreground">
              {t("Explore having WoozyAI manage certification and sales for your portfolio.")}
            </p>
            <Button variant="hero" asChild>
              <LocalizedLink to="/contact" onClick={() => onOpenChange(false)}>
                {t("Check My Portfolio's Eligibility")}
                <ArrowRight className="ms-2 h-4 w-4" aria-hidden="true" />
              </LocalizedLink>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CalculateCarbonButton = ({
  className,
  fullWidth,
  iconOnly,
}: {
  className?: string;
  fullWidth?: boolean;
  iconOnly?: boolean;
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size={iconOnly ? "icon" : fullWidth ? "default" : "sm"}
        className={cn("border-primary/40 text-primary hover:bg-primary/10", fullWidth && "w-full", className)}
        onClick={() => setOpen(true)}
        type="button"
        aria-label={iconOnly ? t("Calculate Carbon Revenue") : undefined}
        title={iconOnly ? t("Calculate Carbon Revenue") : undefined}
      >
        <Leaf className={cn("h-4 w-4", !iconOnly && "me-2")} aria-hidden="true" />
        {!iconOnly && t("Calculate Carbon Revenue")}
      </Button>
      <CarbonCalculator open={open} onOpenChange={setOpen} />
    </>
  );
};

/**
 * Compact inline carbon calculator anchored on the leading side of the page,
 * flush beneath the header. Mirrors the savings mini calculator.
 */
const MiniCarbonCalculator = ({ mobile = false }: { mobile?: boolean }) => {
  const { t } = useLanguage();
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [unit, setUnit] = useState<"kW" | "MW">("MW");
  const [capacity, setCapacity] = useState(10);
  const [countryCode, setCountryCode] = useState("IL");
  const [installYear, setInstallYear] = useState(CURRENT_YEAR - 2);
  const topOffset = 113;

  const result = useMemo(
    () =>
      calculateCarbon({
        countryCode,
        region: getCountry(countryCode).regions[0].name,
        unit,
        capacity,
        installYear,
        certification: "none",
      }),
    [countryCode, unit, capacity, installYear],
  );

  if (dismissed) return null;

  const best = result.certificates.netProceeds ?? result.credits.netProceeds;

  const calculator = (
    <>
      <div
        className={cn(
          mobile
            ? "relative z-10 mx-auto w-full max-w-md lg:hidden"
            : "absolute start-6 z-40 hidden w-80 lg:block motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-700",
        )}
        style={mobile ? undefined : { top: topOffset, maxHeight: `calc(100vh - ${topOffset + 24}px)` }}
      >
        <div
          className={cn(
            "relative h-full rounded-xl border border-primary/40 bg-background/95 shadow-[0_0_28px_rgba(0,240,255,0.28)]",
            mobile ? "p-5" : "overflow-y-auto overscroll-contain p-6",
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2.5">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold leading-snug text-primary">
                {t("Calculate Carbon Revenue with WoozyAI")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="-me-1.5 -mt-1.5 shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={t("Dismiss")}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-5 space-y-5">
            <div>
              <Label htmlFor={`${uid}-cap`} className="text-[13px] text-muted-foreground">
                {t("Total installed solar capacity")}
              </Label>
              <div className="mt-1.5 flex gap-1.5">
                <input
                  id={`${uid}-cap`}
                  type="number"
                  min={0}
                  inputMode="decimal"
                  value={capacity}
                  onChange={(event) => setCapacity(Math.max(0, Number(event.target.value)))}
                  className={cn(fieldClass, "min-w-0 flex-1 py-2.5 text-base")}
                />
                <div className="flex shrink-0 overflow-hidden rounded-lg border border-border">
                  {(["kW", "MW"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setUnit(option)}
                      aria-pressed={unit === option}
                      className={cn(
                        "px-3 text-sm font-semibold",
                        unit === option ? "bg-primary/20 text-primary" : "text-muted-foreground",
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor={`${uid}-country`} className="text-[13px] text-muted-foreground">
                  {t("Country")}
                </Label>
                <select
                  id={`${uid}-country`}
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                  className={cn(fieldClass, "mt-1.5 py-2.5 text-sm")}
                >
                  {COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {t(country.name)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor={`${uid}-year`} className="text-[13px] text-muted-foreground">
                  {t("Installation year")}
                </Label>
                <select
                  id={`${uid}-year`}
                  value={installYear}
                  onChange={(event) => setInstallYear(Number(event.target.value))}
                  className={cn(fieldClass, "mt-1.5 py-2.5 text-sm")}
                >
                  {YEARS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t("Estimated annual avoided emissions")}
              </p>
              <p className="mt-1.5 text-[28px] font-bold leading-tight text-primary" dir="ltr">
                {`${number(result.avoidedTonnes)} tCO2e`}
              </p>
              <p
                className={cn(
                  "mt-2 text-lg font-bold leading-snug",
                  best ? "text-primary" : "text-muted-foreground",
                )}
              >
                {best ? (
                  <>
                    {`${t("Estimated net proceeds")}: `}
                    <span dir="ltr" className="whitespace-nowrap">
                      {range(best)}
                    </span>{" "}
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("per year")}
                    </span>
                  </>
                ) : (
                  t("Eligibility assessment required")
                )}
              </p>
            </div>

            <Button
              variant="outline"
              type="button"
              className="w-full border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => setOpen(true)}
              aria-haspopup="dialog"
            >
              <ShieldQuestion className="me-2 h-4 w-4" aria-hidden="true" />
              {t("Advanced Mode")}
            </Button>

            <p className="text-[11px] italic leading-snug text-muted-foreground">
              {t(
                "Estimates are based on general assumptions and are non-binding; eligibility, savings, and revenue are not guaranteed.",
              )}
            </p>
          </div>
        </div>
      </div>
      <CarbonCalculator open={open} onOpenChange={setOpen} />
    </>
  );

  return mobile ? calculator : createPortal(calculator, document.body);
};

export const CarbonModeCallout = () => <MiniCarbonCalculator />;

export const MobileCarbonCalculator = () => <MiniCarbonCalculator mobile />;

export default CarbonCalculator;
